import { PDF_LAYOUTS, pageCells } from "./pageGeometry.js";

// ─── Иконки-превью ────────────────────────────────────────────────────────────

const IconEmpty = () => (
  <svg viewBox="0 0 40 40" width="34" height="34" fill="none">
    <rect x="2" y="2" width="36" height="36" rx="3" stroke="currentColor" strokeWidth="2" strokeDasharray="4 3" opacity="0.6"/>
  </svg>
);

const IconSingle = () => (
  <svg viewBox="0 0 40 40" width="34" height="34" fill="none">
    <rect x="2" y="2" width="36" height="36" rx="3" fill="currentColor" opacity="0.85"/>
  </svg>
);

const Icon4Grid = () => (
  <svg viewBox="0 0 40 40" width="34" height="34" fill="none">
    <rect x="2"  y="2"  width="16" height="16" rx="2" fill="currentColor" opacity="0.85"/>
    <rect x="22" y="2"  width="16" height="16" rx="2" fill="currentColor" opacity="0.85"/>
    <rect x="2"  y="22" width="16" height="16" rx="2" fill="currentColor" opacity="0.85"/>
    <rect x="22" y="22" width="16" height="16" rx="2" fill="currentColor" opacity="0.85"/>
  </svg>
);

const IconBigLeft = () => (
  <svg viewBox="0 0 40 40" width="34" height="34" fill="none">
    <rect x="2"  y="2"  width="16" height="36" rx="2" fill="currentColor" opacity="0.85"/>
    <rect x="22" y="2"  width="16" height="16" rx="2" fill="currentColor" opacity="0.5"/>
    <rect x="22" y="22" width="16" height="16" rx="2" fill="currentColor" opacity="0.5"/>
  </svg>
);

const IconStripe3 = () => (
  <svg viewBox="0 0 40 40" width="34" height="34" fill="none">
    <rect x="2"  y="2"  width="36" height="10" rx="2" fill="currentColor" opacity="0.85"/>
    <rect x="2"  y="15" width="36" height="10" rx="2" fill="currentColor" opacity="0.85"/>
    <rect x="2"  y="28" width="36" height="10" rx="2" fill="currentColor" opacity="0.85"/>
  </svg>
);

const IconStack2 = () => (
  <svg viewBox="0 0 40 40" width="34" height="34" fill="none">
    <rect x="2" y="2"  width="36" height="16" rx="2" fill="currentColor" opacity="0.85"/>
    <rect x="2" y="22" width="36" height="16" rx="2" fill="currentColor" opacity="0.85"/>
  </svg>
);

const IconPanorama = () => (
  <svg viewBox="0 0 40 40" width="34" height="34" fill="none">
    <rect x="2"  y="2"  width="36" height="22" rx="2" fill="currentColor" opacity="0.85"/>
    <rect x="2"  y="28" width="16" height="10" rx="2" fill="currentColor" opacity="0.5"/>
    <rect x="22" y="28" width="16" height="10" rx="2" fill="currentColor" opacity="0.5"/>
  </svg>
);

// One SVG coordinate system; no nested SVG viewport or offset scaling.
export function PageLayoutIcon({ layout, side = "right", width = 155, height = 210 }) {
  const spec = PDF_LAYOUTS[layout.id];
  const w = spec?.width || width, h = spec?.height || height;
  const rawCells = pageCells(layout, side, w, h);
  // Иконка — условная схема. Центрируем весь блок, сохраняя размер,
  // пропорции, порядок фотографий и промежутки между ними.
  let dx = 0, dy = 0;
  if (layout.framed && rawCells.length) {
    const x0 = Math.min(...rawCells.map(c => c.x));
    const x1 = Math.max(...rawCells.map(c => c.x + c.w));
    const y0 = Math.min(...rawCells.map(c => c.y));
    const y1 = Math.max(...rawCells.map(c => c.y + c.h));
    dx = (1 - x0 - x1) / 2;
    dy = (1 - y0 - y1) / 2;
  }
  const cells = rawCells.map(c => ({ ...c, x: c.x + dx, y: c.y + dy }));
  return <svg viewBox={`0 0 ${w} ${h}`} width="34" height="40" fill="none">
    <rect x="1" y="1" width={w - 2} height={h - 2}
      stroke="currentColor" strokeWidth="2" opacity="0.35" />
    {cells.map((c, i) => <rect key={i} x={c.x * w} y={c.y * h}
      width={c.w * w} height={c.h * h} fill="currentColor" stroke="white" strokeWidth={spec ? 0 : 3} opacity="0.85" />)}
  </svg>;
}

// ─── Конфигурации коллажей ────────────────────────────────────────────────────

export const LAYOUTS = [
  // {
  //   id: "empty",
  //   label: "Пустая",
  //   icon: <IconEmpty />,
  //   /** Нет слотов — просто чистая страница */
  //   rows: [],
  // },

  {
    id: "single",
    label: "1 фото",
    icon: <IconSingle />,
    /** Один слот на всю страницу */
    rows: [
      { height: "100%", slots: [{ flex: 1 }] },
    ],
  },

  {
    id: "grid-4",
    label: "Сетка 2×2",
    icon: <Icon4Grid />,
    /**
     * 2 строки, в каждой 2 ячейки одинаковой ширины
     * Итого: 4 фотографии на страницу
     */
    rows: [
      {
        height: "50%",
        slots: [
          { flex: 1 },
          { flex: 1 },
        ],
      },
      {
        height: "50%",
        slots: [
          { flex: 1 },
          { flex: 1 },
        ],
      },
    ],
  },

  {
    id: "big-left",
    label: "Акцент слева",
    icon: <IconBigLeft />,
    /**
     * 1 строка: большая ячейка слева (flex 1.4) +
     * правая колонка из 2 ячеек (Stack direction="column")
     * Используем специальный флаг `columnSlots` для вертикального стека внутри строки
     */
    rows: [
      {
        height: "100%",
        slots: [
          { flex: 1 },
          {
            flex: 1,
            columnSlots: [
              { flex: 1 },
              { flex: 1 },
            ],
          },
        ],
      },
    ],
  },

  {
    id: "stripe-3",
    label: "3 полосы",
    icon: <IconStripe3 />,
    /**
     * 3 горизонтальные полосы одинаковой высоты
     * Итого: 3 фотографии на страницу
     */
    rows: [
      { height: "33.33%", slots: [{ flex: 1 }] },
      { height: "33.33%", slots: [{ flex: 1 }] },
      { height: "33.33%", slots: [{ flex: 1 }] },
    ],
  },

  {
    id: "stack-2",
    label: "2 фото",
    icon: <IconStack2 />,
    /**
     * Две фотографии друг под другом, каждая на всю ширину страницы.
     * Пропорция 50/50 — держать в синхроне с сервером:
     * main/pdf_generator.py (LAYOUTS["stack-2"]) и
     * worker/page_composer.py (_layout_cells, "stack-2").
     */
    rows: [
      { height: "50%", slots: [{ flex: 1 }] },
      { height: "50%", slots: [{ flex: 1 }] },
    ],
  },

  {
    id: "panorama-top",
    label: "Панорама",
    icon: <IconPanorama />,
    /**
     * Большая панорамная ячейка сверху (60%) +
     * 2 ячейки снизу (40%)
     * Итого: 3 фотографии на страницу
     */
    rows: [
      {
        height: "60%",
        slots: [{ flex: 1 }],
      },
      {
        height: "40%",
        slots: [
          { flex: 1 },
          { flex: 1 },
        ],
      },
    ],
  },

  // ── Листы с белыми краями (framed) ──
  // Та же геометрия, что у базовых, но флаг framed → BookPage добавляет белый
  // отступ по краям листа. id держать в синхроне с сервером:
  // main/models.py (LAYOUT_SLOT_COUNT) и main/pdf_generator.py (LAYOUTS).
  {
    id: "single-framed", label: "1 фото", framed: true,
    icon: null,
    rows: [ { height: "100%", slots: [{ flex: 1 }] } ],
  },
  {
    id: "stack-2-framed", label: "2 фото", framed: true,
    icon: null,
    rows: [
      { height: "50%", slots: [{ flex: 1 }] },
      { height: "50%", slots: [{ flex: 1 }] },
    ],
  },
  {
    id: "grid-4-framed", label: "Сетка 2×2", framed: true,
    icon: null,
    rows: [
      { height: "50%", slots: [{ flex: 1 }, { flex: 1 }] },
      { height: "50%", slots: [{ flex: 1 }, { flex: 1 }] },
    ],
  },
  {
    id: "big-left-framed", label: "Акцент слева", framed: true,
    icon: null,
    rows: [
      {
        height: "100%",
        slots: [
          { flex: 1 },
          { flex: 1, columnSlots: [{ flex: 1 }, { flex: 1 }] },
        ],
      },
    ],
  },
  {
    id: "stripe-3-framed", label: "3 полосы", framed: true,
    icon: null,
    rows: [
      { height: "33.33%", slots: [{ flex: 1 }] },
      { height: "33.33%", slots: [{ flex: 1 }] },
      { height: "33.33%", slots: [{ flex: 1 }] },
    ],
  },
  {
    id: "panorama-top-framed", label: "Панорама", framed: true,
    icon: null,
    rows: [
      { height: "60%", slots: [{ flex: 1 }] },
      { height: "40%", slots: [{ flex: 1 }, { flex: 1 }] },
    ],
  },
];

for (const [id, spec] of Object.entries(PDF_LAYOUTS)) {
  LAYOUTS.push({ id, label: spec.label, framed: true, pdf: true,
    rows: [{ height: "100%", slots: spec.cells.map(() => ({ flex: 1 })) }],
  });
}
for (const layout of LAYOUTS.filter(l => l.framed)) {
  layout.icon = <PageLayoutIcon layout={layout} />;
}
export function framedLayoutsForSize(bookSize) {
  const format = Number(bookSize?.heightMm || 210) >= 230 ? "a4" : "a5";
  return LAYOUTS.filter(l => l.pdf && PDF_LAYOUTS[l.id].format === format);
}

// Базовые макеты (без белых краёв) и framed-макеты (с белыми краями) —
// для двух отдельных секций в пикере. Рендер листа продолжает искать по
// полному LAYOUTS (getLayoutById), поэтому оба вида находятся.
export const BASE_LAYOUTS   = LAYOUTS.filter(l => !l.framed);
// Руководитель оставил в «листах с белыми краями» только 3 макета.
const FRAMED_KEEP = ["single-framed", "stack-2-framed", "stripe-3-framed"];
export const FRAMED_LAYOUTS = LAYOUTS.filter(l => l.framed && FRAMED_KEEP.includes(l.id));

// ─── Хелпер: считаем общее кол-во слотов в layout ────────────────────────────

export function countSlots(layout) {
  return layout.rows.reduce((total, row) => {
    return total + row.slots.reduce((rowTotal, slot) => {
      if (slot.columnSlots) return rowTotal + slot.columnSlots.length;
      return rowTotal + 1;
    }, 0);
  }, 0);
}

// ─── Хелпер: получить layout по id ───────────────────────────────────────────

export function getLayoutById(id) {
  return LAYOUTS.find(l => l.id === id) ?? LAYOUTS[0];
}


export const FIT_OPTIONS = [
  { value: "cover",   label: "Заполнить (cover)" },
  { value: "contain", label: "Вписать (contain)" },
  { value: "fill",    label: "Растянуть (fill)" },
  { value: "none",    label: "Оригинал (none)" },
];

export const POSITION_OPTIONS = [
  { value: "center",       label: "По центру" },
  { value: "top",          label: "Сверху" },
  { value: "bottom",       label: "Снизу" },
  { value: "left",         label: "Слева" },
  { value: "right",        label: "Справа" },
  { value: "top left",     label: "Верхний левый" },
  { value: "top right",    label: "Верхний правый" },
  { value: "bottom left",  label: "Нижний левый" },
  { value: "bottom right", label: "Нижний правый" },
];



export function getSlotEdges(layout, slotIndex) {
  if (!layout?.rows?.length) {
    return { top: true, right: true, bottom: true, left: true };
  }

  let si = 0;
  for (let rIdx = 0; rIdx < layout.rows.length; rIdx++) {
    const row = layout.rows[rIdx];
    const isFirstRow = rIdx === 0;
    const isLastRow  = rIdx === layout.rows.length - 1;

    for (let sIdx = 0; sIdx < row.slots.length; sIdx++) {
      const slot = row.slots[sIdx];
      const isFirstSlotInRow = sIdx === 0;
      const isLastSlotInRow  = sIdx === row.slots.length - 1;

      if (slot.columnSlots) {
        for (let cIdx = 0; cIdx < slot.columnSlots.length; cIdx++) {
          if (si === slotIndex) {
            const isFirstCol = cIdx === 0;
            const isLastCol  = cIdx === slot.columnSlots.length - 1;
            return {
              top:    isFirstRow && isFirstCol,
              bottom: isLastRow  && isLastCol,
              left:   isFirstSlotInRow,
              right:  isLastSlotInRow,
            };
          }
          si++;
        }
      } else {
        if (si === slotIndex) {
          return {
            top:    isFirstRow,
            bottom: isLastRow,
            left:   isFirstSlotInRow,
            right:  isLastSlotInRow,
          };
        }
        si++;
      }
    }
  }
  return { top: true, right: true, bottom: true, left: true };
}